import { useBookerStoreContext } from "@calcom/features/bookings/Booker/BookerStoreProvider";
import type { BookerEvent } from "@calcom/features/bookings/types";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { SchedulingType } from "@calcom/prisma/enums";
import { Select } from "@calcom/ui/components/form";
import { shallow } from "zustand/shallow";

type ProviderOption = { label: string; value: string | null };

/**
 * Opt-in provider picker. Renders only when the event type sets
 * `metadata.allowAttendeeToSelectHost` and is a round-robin team event with multiple hosts.
 * When absent (the default), nothing is rendered and the booking flow is unchanged.
 *
 * The selected host is tracked by public username in the booker store, which the slots query
 * reads reactively; the server resolves the username to the host privately.
 */
export const ProviderPicker = ({
  event,
}: {
  event?: Pick<BookerEvent, "schedulingType" | "subsetOfUsers" | "metadata"> | null;
}) => {
  const { t } = useLocale();
  const [teamMemberUsername, setTeamMemberUsername] = useBookerStoreContext(
    (state) => [state.teamMemberUsername, state.setTeamMemberUsername],
    shallow
  );

  if (!event) return null;

  const isOptedIn = event.metadata?.allowAttendeeToSelectHost === true;
  const isRoundRobin = event.schedulingType === SchedulingType.ROUND_ROBIN;
  const hosts = (event.subsetOfUsers ?? []).filter((user) => Boolean(user.username));

  // Only meaningful for an opted-in round-robin event with more than one selectable host.
  if (!isOptedIn || !isRoundRobin || hosts.length < 2) return null;

  const anyoneOption: ProviderOption = { label: t("anyone_available"), value: null };
  const options: ProviderOption[] = [
    anyoneOption,
    ...hosts.map((user) => ({
      label: user.name || user.username || "",
      value: user.username as string,
    })),
  ];

  const selected = options.find((option) => option.value === (teamMemberUsername ?? null)) ?? anyoneOption;

  return (
    <div className="mb-4" data-testid="provider-picker">
      <label className="text-default mb-1 block text-sm font-medium" htmlFor="provider-picker-select">
        {t("choose_a_team_member")}
      </label>
      <Select<ProviderOption>
        inputId="provider-picker-select"
        isSearchable={hosts.length > 6}
        value={selected}
        options={options}
        onChange={(option) => setTeamMemberUsername(option?.value ?? null)}
      />
    </div>
  );
};
